import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button } from 'react-bootstrap';
import { FaUsers, FaCalendarDay, FaUserCheck, FaPhoneAlt, FaExclamationCircle, FaChartPie, FaRocket, FaRegClock, FaUserPlus, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { clientApi } from '../services/api';
import { CLIENT_STATUSES } from '../utils/clientModel';

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    leads: 0,
    connectedPending: 0,
    connectedFollowUp: 0,
    todayFollowUps: 0,
    unseenClients: 0,
    statusDistribution: {},
    recentClients: [],
    upcomingFollowUps: []
  });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clients = await clientApi.getAll();
        
        // Calculate statistics based on new status values
        const leads = clients.filter(c => c.status === 'Lead').length;
        const connectedPending = clients.filter(c => c.status === 'Connected (Pending)').length;
        const connectedFollowUp = clients.filter(c => c.status === 'Connected (Follow-Up)').length;
        
        // Calculate status distribution
        const statusDistribution = {};
        Object.values(CLIENT_STATUSES).forEach(status => {
          statusDistribution[status] = clients.filter(c => c.status === status).length;
        });
        
        // Get today's date (without time)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        // Find clients with follow-ups today
        const todayFollowUps = clients.filter(client => {
          if (!client.nextFollowUp) return false;
          const followUpDate = new Date(client.nextFollowUp);
          followUpDate.setHours(0, 0, 0, 0);
          return followUpDate.getTime() === today.getTime();
        });
        
        // Find clients that haven't been seen yet (no firstContact)
        const unseenClients = clients.filter(client => !client.firstContact);
        
        // Get recent clients (sorted by creation date)
        const recentClients = [...clients]
          .sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dateB - dateA;
          })
          .slice(0, 5);
        
        // Get upcoming follow-ups (next 7 days)
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        const upcomingFollowUps = clients
          .filter(client => {
            if (!client.nextFollowUp) return false;
            const followUpDate = new Date(client.nextFollowUp);
            return followUpDate >= today && followUpDate <= nextWeek;
          })
          .sort((a, b) => new Date(a.nextFollowUp) - new Date(b.nextFollowUp))
          .slice(0, 5);
        
        setStats({
          totalClients: clients.length,
          leads,
          connectedPending,
          connectedFollowUp,
          todayFollowUps: todayFollowUps.length,
          unseenClients: unseenClients.length,
          statusDistribution,
          recentClients,
          upcomingFollowUps
        });
      } catch (error) {
        console.error('Error fetching clients for dashboard:', error);
      }
    };
    
    fetchClients();
  }, []);

  return (
    <Layout>
      <div className="dashboard-header mb-4">
        <h2 className="dashboard-title">Client Dashboard</h2>
        <p className="dashboard-subtitle">Overview of your client management system</p>
      </div>
      
      <Row className="mb-4 g-3">
        <Col md={3} sm={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="stat-icon-wrapper primary">
                <FaUsers className="stat-icon" />
              </div>
              <div className="stat-content">
                <h2 className="stat-value">{stats.totalClients}</h2>
                <p className="stat-label">Total Clients</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="stat-icon-wrapper info">
                <FaRocket className="stat-icon" />
              </div>
              <div className="stat-content">
                <h2 className="stat-value">{stats.leads}</h2>
                <p className="stat-label">Leads</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="stat-icon-wrapper warning">
                <FaPhoneAlt className="stat-icon" />
              </div>
              <div className="stat-content">
                <h2 className="stat-value">{stats.connectedPending}</h2>
                <p className="stat-label">Connected (Pending)</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="stat-icon-wrapper success">
                <FaUserCheck className="stat-icon" />
              </div>
              <div className="stat-content">
                <h2 className="stat-value">{stats.connectedFollowUp}</h2>
                <p className="stat-label">Connected (Follow-Up)</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4 g-3">
        <Col md={3} sm={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="stat-icon-wrapper danger">
                <FaExclamationCircle className="stat-icon" />
              </div>
              <div className="stat-content">
                <h2 className="stat-value">{stats.unseenClients}</h2>
                <p className="stat-label">Unseen Clients</p>
              </div>
              <Link to="/" className="stretched-link"></Link>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} sm={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="stat-icon-wrapper accent">
                <FaCalendarDay className="stat-icon" />
              </div>
              <div className="stat-content">
                <h2 className="stat-value">{stats.todayFollowUps}</h2>
                <p className="stat-label">Today's Follow-ups</p>
              </div>
              <Link to="/" className="stretched-link"></Link>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-subtitle">Status Distribution</h5>
              </div>
              <div className="status-distribution">
                {Object.entries(stats.statusDistribution).map(([status, count]) => (
                  count > 0 && (
                    <div key={status} className="status-item">
                      <div className="d-flex justify-content-between">
                        <span className="status-name">{status}</span>
                        <span className="status-count">{count}</span>
                      </div>
                      <div className="status-bar-bg">
                        <div 
                          className={`status-bar status-${status.toLowerCase().replace(/[()\s]/g, '-')}`}
                          style={{ width: `${(count / stats.totalClients) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Status Distribution</h5>
            </Card.Header>
            <Card.Body>
              {Object.entries(stats.statusDistribution).map(([status, count]) => (
                <div key={status} className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>{status}</span>
                    <span>{count} clients</span>
                  </div>
                  <div className="progress">
                    <div 
                      className={`progress-bar bg-${getStatusColor(status)}`}
                      role="progressbar" 
                      style={{ 
                        width: `${stats.totalClients ? (count / stats.totalClients) * 100 : 0}%` 
                      }}
                      aria-valuenow={count} 
                      aria-valuemin="0" 
                      aria-valuemax={stats.totalClients}
                    />
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Recently Added Clients</h5>
            </Card.Header>
            <Card.Body>
              {stats.recentClients.length === 0 ? (
                <p className="text-muted">No clients added yet.</p>
              ) : (
                <div className="list-group">
                  {stats.recentClients.map(client => (
                    <a 
                      key={client.id} 
                      href={`/client/${client.id}`} 
                      className="list-group-item list-group-item-action"
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">{client.firstName} {client.lastName}</h6>
                        <small className="text-muted">
                          {new Date(client.createdAt).toLocaleDateString()}
                        </small>
                      </div>
                      <p className="mb-1">{client.company || 'No company'}</p>
                      <small className="text-muted">Status: {client.status}</small>
                    </a>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Layout>
  );
};

// Helper function to get Bootstrap color for status
const getStatusColor = (status) => {
  switch (status) {
    case CLIENT_STATUSES.ACTIVE:
      return 'success';
    case CLIENT_STATUSES.INACTIVE:
      return 'secondary';
    case CLIENT_STATUSES.LEAD:
      return 'info';
    case CLIENT_STATUSES.PROSPECT:
      return 'primary';
    case CLIENT_STATUSES.CUSTOMER:
      return 'warning';
    case CLIENT_STATUSES.FORMER:
      return 'danger';
    default:
      return 'light';
  }
};

export default DashboardPage;
